const NPI_SEARCH_ENDPOINT = "https://clinicaltables.nlm.nih.gov/api/npi_idv/v3/search";

export interface ProfessionalMatch {
  npi: string;
  name: string;
  providerType: string;
  practiceAddress: string;
}

export type ProfessionalVerificationResult =
  | { status: "confirmed"; match: ProfessionalMatch }
  | { status: "not_confirmed" };

export function isValidProfessionalName(name: string): boolean {
  return name.trim().split(/\s+/).filter(Boolean).length >= 2;
}

export function isValidUsZip(zip: string): boolean {
  return /^\d{5}(?:-\d{4})?$/.test(zip.trim());
}

export async function verifyProfessional(
  name: string,
  zip: string,
  signal?: AbortSignal,
): Promise<ProfessionalVerificationResult> {
  if (!isValidProfessionalName(name) || !isValidUsZip(zip)) {
    return { status: "not_confirmed" };
  }

  const fiveDigitZip = zip.trim().slice(0, 5);
  const params = new URLSearchParams({
    terms: `${name.trim()} ${fiveDigitZip}`,
    maxList: "10",
    df: "NPI,name.full,provider_type,addr_practice.full",
  });
  const response = await fetch(`${NPI_SEARCH_ENDPOINT}?${params.toString()}`, {
    method: "GET",
    signal,
  });

  if (!response.ok) {
    throw new Error(`NPI verification failed with status ${response.status}`);
  }

  const payload: unknown = await response.json();
  if (!Array.isArray(payload) || !Array.isArray(payload[3]) || payload[3].length === 0) {
    return { status: "not_confirmed" };
  }

  const row = payload[3][0];
  if (!Array.isArray(row) || row.length < 4 || row.some((value) => typeof value !== "string")) {
    return { status: "not_confirmed" };
  }

  return {
    status: "confirmed",
    match: {
      npi: row[0],
      name: row[1],
      providerType: row[2],
      practiceAddress: row[3],
    },
  };
}

